import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bot } from "lucide-react";

/**
 * Floating "kai" button — appears bottom-right on every page.
 *
 * Behaviour:
 *   • Clicking always brings the user to the KAI chat section on the home
 *     page (/#kai). If the user is already on the home page, it simply
 *     smooth-scrolls to that section instead of triggering a full navigation.
 *   • The button auto-hides once the KAI chat section is on screen so it
 *     never overlaps the chat itself. It reappears as soon as the section
 *     scrolls out of view.
 *   • Hidden on admin routes to keep that UI focused.
 */
export default function KaiFloatingButton() {
  const { pathname } = useLocation();
  const [sectionVisible, setSectionVisible] = React.useState(false);

  // observe the home-page KAI section and hide the FAB when it's on screen
  React.useEffect(() => {
    if (pathname !== "/") {
      setSectionVisible(false);
      return;
    }
    const el = document.getElementById("kai");
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setSectionVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: "-80px 0px -80px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [pathname]);

  const isAdmin = pathname.startsWith("/admin");
  const show = !isAdmin && !sectionVisible;

  const onClick = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      document.getElementById("kai")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    // otherwise let react-router navigate to "/#kai" and let the app scroll
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="kai-fab"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.9 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 z-40"
          data-testid="kai-floating-btn-wrapper"
        >
          <Link
            to="/#kai"
            onClick={onClick}
            aria-label="open kai — ai consultant"
            data-testid="kai-floating-btn"
            className="group relative inline-flex items-center gap-2.5 pl-3 pr-5 py-3 rounded-full text-white font-medium text-sm bg-gradient-to-b from-blue-500 to-blue-600 shadow-[0_10px_30px_-6px_rgba(37,99,235,0.55)] hover:shadow-[0_14px_36px_-4px_rgba(59,130,246,0.9)] hover:from-blue-400 hover:to-blue-600 transition-all"
          >
            <span className="absolute inset-0 rounded-full bg-blue-500/40 blur-lg -z-10 group-hover:bg-blue-400/60 transition" />
            <span className="relative h-8 w-8 rounded-full bg-white/15 border border-white/25 flex items-center justify-center">
              <Bot size={16} />
              <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 border-2 border-blue-600 animate-pulse" />
            </span>
            <span className="font-display lowercase font-bold tracking-tight">kai</span>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
